/**
 * Composant racine UNIQUE SaaS.
 * - Si non authentifié : SplashScreen (login + register).
 * - Sinon : layout Sidebar + Header + écran actif (routeur navStore).
 * - Active le moteur d'escalade automatique en tâche de fond.
 */
import { useEffect } from 'react';
import type { JSX } from 'react';
import SplashScreen from '@screens/SplashScreen';
import BriefingScreen from '@screens/BriefingScreen';
import DashboardScreen from '@screens/DashboardScreen';
import FocusWorkScreen from '@screens/FocusWorkScreen';
import CommHubScreen from '@screens/CommHubScreen';
import SmartSaveScreen from '@screens/SmartSaveScreen';
import AnalyticsScreen from '@screens/AnalyticsScreen';
import SettingsScreen from '@screens/SettingsScreen';
import PricingScreen from '@screens/PricingScreen';
import BillingScreen from '@screens/BillingScreen';
import { DashboardHeader } from '@components/DashboardHeader';
import { Sidebar } from '@components/Sidebar';
import { EscalationToast } from '@components/EscalationToast';
import { useUserStore, createDemoProfile } from '@store/userStore';
import { useTaskStore, buildDemoTasks } from '@store/taskStore';
import { useNavStore } from '@store/navStore';
import { useAutoEscalation } from '@/hooks/useAutoEscalation';

export default function App(): JSX.Element {
  const isAuthenticated = useUserStore((s) => s.isAuthenticated);
  const setProfile = useUserStore((s) => s.setProfile);
  const hydrate = useTaskStore((s) => s.hydrate);
  const tasksLoaded = useTaskStore((s) => s.tasks.length > 0);
  const route = useNavStore((s) => s.route);

  useEffect(() => {
    if (isAuthenticated && !tasksLoaded) {
      hydrate(buildDemoTasks());
    }
  }, [isAuthenticated, tasksLoaded, hydrate]);

  if (!isAuthenticated) {
    return (
      <SplashScreen
        onSuccess={(email) => {
          // Si l'auth réelle a déjà peuplé le store (login/register), c'est un no-op.
          // Sinon (fallback dev sans backend) on crée un profil démo.
          const state = useUserStore.getState();
          if (!state.profile) {
            setProfile(createDemoProfile(email));
          }
        }}
      />
    );
  }

  return <AuthenticatedApp route={route} />;
}

function AuthenticatedApp({ route }: { route: string }): JSX.Element {
  const notices = useAutoEscalation();

  // Les écrans Pricing et Billing n'ont pas de Sidebar/Header
  if (route === 'pricing') return <PricingScreen />;
  if (route === 'billing') return <BillingScreen />;

  return (
    <div className="unique-surface min-h-screen flex flex-col">
      <DashboardHeader />
      <EscalationToast notices={notices} />
      <div className="flex flex-1">
        <Sidebar />
        <main className="flex-1 min-w-0">
          {route === 'briefing'   && <BriefingScreen />}
          {route === 'dashboard'  && <DashboardScreen />}
          {route === 'focus'      && <FocusWorkScreen />}
          {route === 'commhub'    && <CommHubScreen />}
          {route === 'smartsave'  && <SmartSaveScreen />}
          {route === 'analytics'  && <AnalyticsScreen />}
          {route === 'settings'   && <SettingsScreen />}
        </main>
      </div>
    </div>
  );
}
